const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const EXPIRES_IN_KEY = "expiresIn";
const ACCESS_TOKEN_EXPIRES_AT_KEY = "accessTokenExpiresAt";
const LAST_LOGIN_AT_KEY = "lastLoginAt";
const USERNAME_KEY = "username";
const MAIL_KEY = "mail";
const LEGACY_USERNAME_KEY = "DesRepresentante";

const REFRESH_LEEWAY_MS = 30 * 1000;

const hasWindow = typeof window !== "undefined";

const dispatchAuthChanged = () => {
	if (!hasWindow) return;

	window.dispatchEvent(new Event("planaett-auth-changed"));
};

const removeFromStorage = (storage, keys) => {
	keys.forEach((key) => storage.removeItem(key));
};

const writeToStorage = (storage, key, value) => {
	if (value === undefined || value === null || value === "") {
		storage.removeItem(key);
		return;
	}

	storage.setItem(key, String(value));
};

const getStorageValue = (key) => {
	const localValue = localStorage.getItem(key);
	if (localValue !== null) return localValue;

	const sessionValue = sessionStorage.getItem(key);
	return sessionValue !== null ? sessionValue : null;
};

const getPreferredStorage = () => {
	if (
		localStorage.getItem(REFRESH_TOKEN_KEY) ||
		localStorage.getItem(ACCESS_TOKEN_KEY)
	) {
		return localStorage;
	}

	if (
		sessionStorage.getItem(REFRESH_TOKEN_KEY) ||
		sessionStorage.getItem(ACCESS_TOKEN_KEY)
	) {
		return sessionStorage;
	}

	return localStorage;
};

const getExpiresAtValue = (expiresIn) => {
	const seconds = Number(expiresIn);
	if (!Number.isFinite(seconds) || seconds <= 0) return null;

	return Date.now() + seconds * 1000;
};

const clearAuthKeys = () => {
	removeFromStorage(localStorage, [
		ACCESS_TOKEN_KEY,
		REFRESH_TOKEN_KEY,
		EXPIRES_IN_KEY,
		ACCESS_TOKEN_EXPIRES_AT_KEY,
		LAST_LOGIN_AT_KEY,
		USERNAME_KEY,
		MAIL_KEY,
		LEGACY_USERNAME_KEY,
	]);
	removeFromStorage(sessionStorage, [
		ACCESS_TOKEN_KEY,
		REFRESH_TOKEN_KEY,
		EXPIRES_IN_KEY,
		ACCESS_TOKEN_EXPIRES_AT_KEY,
		LAST_LOGIN_AT_KEY,
	]);
};

export const getAccessToken = () => getStorageValue(ACCESS_TOKEN_KEY) || "";

export const getRefreshToken = () => getStorageValue(REFRESH_TOKEN_KEY) || "";

export const getAccessTokenExpiresAt = () => {
	const value = Number(getStorageValue(ACCESS_TOKEN_EXPIRES_AT_KEY));
	return Number.isFinite(value) && value > 0 ? value : 0;
};

export const getLastLoginAt = () => {
	const value = Number(getStorageValue(LAST_LOGIN_AT_KEY));
	return Number.isFinite(value) && value > 0 ? value : 0;
};

export const hasAuthSession = () => Boolean(getAccessToken() || getRefreshToken());

export const getAuthStorageUserScope = () => {
	const mail = getStorageValue(MAIL_KEY);
	if (mail) {
		return `mail:${mail.trim().toLowerCase()}`;
	}

	const username = getStorageValue(USERNAME_KEY) || getStorageValue(LEGACY_USERNAME_KEY);
	if (username) {
		return `user:${username.trim().toLowerCase()}`;
	}

	return "anonymous";
};

export const isAccessTokenExpiringSoon = (bufferMs = REFRESH_LEEWAY_MS) => {
	const accessToken = getAccessToken();
	const refreshToken = getRefreshToken();

	if (!accessToken) {
		return Boolean(refreshToken);
	}

	const expiresAt = getAccessTokenExpiresAt();
	if (!expiresAt) {
		return false;
	}

	return Date.now() + bufferMs >= expiresAt;
};

export const wasAuthSessionRecentlyCreated = (windowMs = 15000) => {
	const lastLoginAt = getLastLoginAt();
	if (!lastLoginAt) {
		return false;
	}

	return Date.now() - lastLoginAt <= windowMs;
};

export const persistAuthSession = ({
	accessToken,
	refreshToken,
	expiresIn,
	username,
	mail,
} = {}) => {
	const storage = localStorage;

	clearAuthKeys();

	writeToStorage(storage, ACCESS_TOKEN_KEY, accessToken);
	writeToStorage(storage, REFRESH_TOKEN_KEY, refreshToken);
	writeToStorage(storage, EXPIRES_IN_KEY, expiresIn);
	writeToStorage(storage, ACCESS_TOKEN_EXPIRES_AT_KEY, getExpiresAtValue(expiresIn));
	writeToStorage(storage, LAST_LOGIN_AT_KEY, Date.now());

	writeToStorage(localStorage, USERNAME_KEY, username);
	writeToStorage(localStorage, MAIL_KEY, mail);

	dispatchAuthChanged();
};

export const updateAccessToken = ({ accessToken, refreshToken, expiresIn } = {}) => {
	const storage = getPreferredStorage();

	writeToStorage(storage, ACCESS_TOKEN_KEY, accessToken);
	writeToStorage(storage, REFRESH_TOKEN_KEY, refreshToken || getRefreshToken());
	writeToStorage(storage, EXPIRES_IN_KEY, expiresIn);
	writeToStorage(storage, ACCESS_TOKEN_EXPIRES_AT_KEY, getExpiresAtValue(expiresIn));

	dispatchAuthChanged();

	return accessToken || "";
};

export const clearAuthSession = () => {
	clearAuthKeys();
	dispatchAuthChanged();
};
